import React, { useEffect } from 'react'
import { isNil } from 'lodash'
import { useRouterContext } from 'components/RouterContext'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { FINANCE_PATH } from '../../constants'
import { Alert, Tabs } from '@surecompanies/core_components'
import type { TabsProps } from '@surecompanies/core_components'
import { Tooltip } from 'antd'
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined'
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import SendOutlined from '@ant-design/icons/SendOutlined'
import SyncOutlined from '@ant-design/icons/SyncOutlined'
import WarningOutlined from '@ant-design/icons/WarningOutlined'
import { InvoiceUploadSection } from './InvoiceUploadSection'
import { InvoiceDeliveryTable } from './InvoiceDeliveryTable'
import { InvoiceDeliveryDrawer } from './InvoiceDeliveryDrawer'
import { ResolveUnmatchedModal } from './ResolveUnmatchedModal'
import { ReviewBeforeSendModal } from './ReviewBeforeSendModal'
import useInvoiceDelivery from './useInvoiceDelivery'
import { useCanDownloadFromInvoiceManagement } from './useCanDownloadFromInvoiceManagement'
import { InvoiceGenerationView } from './generation/InvoiceGenerationView'
import { DeliveryModeContentWithStripePayment } from './DeliveryModeContentWithStripePayment'
import {
  PageContent,
  SectionActions,
  SectionHeader,
  SectionTitle,
  DownloadPeriodButton,
  ResyncButton,
  StatCard,
  StatIcon,
  StatLabel,
  StatValue,
  StatsRow,
  BannerDescription,
  BannerHeader,
  BannerTextStack,
  BannerTitle,
  UnmatchedBanner,
  UnmatchedBannerActions,
  UnmatchedBannerCopy,
  UnmatchedBannerDismissButton,
  UnmatchedBannerPrimaryButton,
  WarningBanner,
  WarningBannerActions,
  WarningBannerCopy,
  WarningBannerDismissButton,
  WarningBannerPrimaryButton,
} from './BulkInvoiceUpload.styles'

type InvoiceManagementMode = 'delivery' | 'generation' | 'delivery-and-payment'

const INVOICE_MANAGEMENT_MODES: readonly InvoiceManagementMode[] = [
  'delivery',
  'generation',
  'delivery-and-payment',
]

const isInvoiceManagementMode = (
  value: unknown
): value is InvoiceManagementMode =>
  INVOICE_MANAGEMENT_MODES.some((mode) => mode === value)

const DeliveryModeContent: React.FC = () => {
  const {
    billingMonth,
    billingYear,
    billingPeriodStart,
    billingPeriodLabel,
    handleBillingMonthChange,
    handleBillingYearChange,

    deliveries,
    monthDeliveries,
    totalDeliveries,
    getTemplateRowInfo,
    isLoading,
    isError,
    stats,

    search,
    setSearch,
    statusFilter,
    handleStatusFilter,
    templateFilter,
    setTemplateFilter,
    allowedTemplates,
    isBannerVisible,
    handleDismissBanner,
    isWarningBannerVisible,
    handleDismissWarningBanner,
    showArchived,
    handleToggleArchived,

    resolveModalOpen,
    handleOpenResolveModal,
    handleOpenFirstWithWarnings,
    handleCloseResolveModal,

    isMutating,
    sendingIds,
    isBulkSending,
    isBulkSyncing,
    handleSend,
    handleBulkSync,
    handleBulkSend,
    handleDelete,
    handleSetArchived,
    handleUploadComplete,

    isDownloadingArchive,
    downloadedBytesLabel,
    handleDownloadSelected,
    handleDownloadMonth,

    selectedRowKeys,
    selectedSendableCount,
    handleSelectedRowKeysChange,

    drawerDelivery,
    currentDeliveryIndex,
    totalInDrawerList,
    canNavigatePrev,
    canNavigateNext,
    handleRowClick,
    handleDrawerClose,
    handleDrawerNavigate,
    reviewModal,
    handleCloseReview,
    handleReviewSent,
  } = useInvoiceDelivery()
  const canDownloadInvoices = useCanDownloadFromInvoiceManagement()

  return (
    <>
      <InvoiceUploadSection
        billingMonth={billingMonth}
        billingYear={billingYear}
        onBillingMonthChange={handleBillingMonthChange}
        onBillingYearChange={handleBillingYearChange}
        onUploadComplete={handleUploadComplete}
      />

      {isBannerVisible && (
        <UnmatchedBanner role="alert">
          <UnmatchedBannerCopy>
            <BannerTextStack>
              <BannerHeader>
                <WarningOutlined aria-hidden />
                <BannerTitle>
                  {stats.unmatched === 1
                    ? '1 unmatched invoice'
                    : `${stats.unmatched} unmatched invoices`}
                </BannerTitle>
              </BannerHeader>
              <BannerDescription>
                Assign a company before sending.
              </BannerDescription>
            </BannerTextStack>
          </UnmatchedBannerCopy>
          <UnmatchedBannerActions>
            <UnmatchedBannerPrimaryButton
              type="button"
              onClick={handleOpenResolveModal}
            >
              Resolve unmatched
            </UnmatchedBannerPrimaryButton>
            <UnmatchedBannerDismissButton
              type="button"
              aria-label="Dismiss unmatched banner"
              onClick={handleDismissBanner}
            >
              <CloseOutlined />
            </UnmatchedBannerDismissButton>
          </UnmatchedBannerActions>
        </UnmatchedBanner>
      )}

      {isWarningBannerVisible && (
        <WarningBanner role="alert">
          <WarningBannerCopy>
            <BannerTextStack>
              <BannerHeader>
                <WarningOutlined aria-hidden />
                <BannerTitle>
                  {stats.withWarnings === 1
                    ? '1 invoice has unreadable cells'
                    : `${stats.withWarnings} invoices have unreadable cells`}
                </BannerTitle>
              </BannerHeader>
              <BannerDescription>
                Fix the values before sending or the email will go out with the
                wrong amount.
              </BannerDescription>
            </BannerTextStack>
          </WarningBannerCopy>
          <WarningBannerActions>
            <WarningBannerPrimaryButton
              type="button"
              onClick={handleOpenFirstWithWarnings}
              disabled={stats.withWarnings === 0}
            >
              Resolve issues
            </WarningBannerPrimaryButton>
            <WarningBannerDismissButton
              type="button"
              aria-label="Dismiss warning banner"
              onClick={handleDismissWarningBanner}
            >
              <CloseOutlined />
            </WarningBannerDismissButton>
          </WarningBannerActions>
        </WarningBanner>
      )}

      <StatsRow>
        <StatCard>
          <div>
            <StatLabel>Total</StatLabel>
            <StatValue>{stats.total}</StatValue>
          </div>
          <StatIcon>
            <FileTextOutlined />
          </StatIcon>
        </StatCard>

        <StatCard>
          <div>
            <StatLabel>Draft</StatLabel>
            <StatValue>{stats.draft}</StatValue>
          </div>
          <StatIcon>
            <ClockCircleOutlined />
          </StatIcon>
        </StatCard>

        <StatCard>
          <div>
            <StatLabel>Sending</StatLabel>
            <StatValue>{stats.sending}</StatValue>
          </div>
          <StatIcon>
            <SendOutlined />
          </StatIcon>
        </StatCard>

        <StatCard>
          <div>
            <StatLabel>Delivered</StatLabel>
            <StatValue>{stats.delivered}</StatValue>
          </div>
          <StatIcon>
            <CheckCircleOutlined />
          </StatIcon>
        </StatCard>
      </StatsRow>

      {isError && (
        <Alert
          type="error"
          message="Failed to load deliveries. Please refresh the page."
          showIcon
        />
      )}

      <SectionHeader>
        <SectionTitle>Invoice deliveries</SectionTitle>
        <SectionActions>
          {stats.sending > 0 && (
            <ResyncButton
              onClick={handleBulkSync}
              disabled={isMutating || isBulkSyncing}
            >
              <SyncOutlined spin={isBulkSyncing} />
              Resync stuck deliveries
            </ResyncButton>
          )}
          {/*
            Period-scoped, so it belongs with the section title rather than the
            table's filter row — it deliberately ignores the status filter,
            search text and archive toggle.
          */}
          {canDownloadInvoices && (
            <Tooltip
              title={`Download all ${billingPeriodLabel} invoices as a ZIP. Ignores the filters below.`}
            >
              <DownloadPeriodButton
                onClick={handleDownloadMonth}
                disabled={isDownloadingArchive || totalDeliveries === 0}
              >
                {isDownloadingArchive ? (
                  <LoadingOutlined />
                ) : (
                  <DownloadOutlined />
                )}
                {/*
                  While in flight, show bytes received instead of the static
                  label — a big archive can take a while, and a bare spinner
                  gives no sign it is still making progress.
                */}
                {isDownloadingArchive
                  ? `Downloading… ${downloadedBytesLabel ?? ''}`
                  : `Download ${billingPeriodLabel} Invoices`}
              </DownloadPeriodButton>
            </Tooltip>
          )}
        </SectionActions>
      </SectionHeader>

      <InvoiceDeliveryTable
        deliveries={deliveries}
        monthDeliveries={monthDeliveries}
        total={totalDeliveries}
        isLoading={isLoading}
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilter}
        templateFilter={templateFilter}
        templateOptions={allowedTemplates}
        onTemplateFilterChange={setTemplateFilter}
        showArchived={showArchived}
        onToggleArchived={handleToggleArchived}
        onSend={handleSend}
        onBulkSend={handleBulkSend}
        onDelete={handleDelete}
        onSetArchived={handleSetArchived}
        onRowClick={handleRowClick}
        selectedRowKeys={selectedRowKeys}
        selectedSendableCount={selectedSendableCount}
        onSelectedRowKeysChange={handleSelectedRowKeysChange}
        isMutating={isMutating}
        sendingIds={sendingIds}
        isBulkSending={isBulkSending}
        canDownloadInvoices={canDownloadInvoices}
        onDownloadSelected={handleDownloadSelected}
        isDownloadingArchive={isDownloadingArchive}
        downloadedBytesLabel={downloadedBytesLabel}
        getTemplateRowInfo={getTemplateRowInfo}
      />

      <InvoiceDeliveryDrawer
        delivery={drawerDelivery}
        monthDeliveries={monthDeliveries}
        open={!isNil(drawerDelivery)}
        onClose={handleDrawerClose}
        onSetArchived={handleSetArchived}
        onDelete={handleDelete}
        currentIndex={currentDeliveryIndex}
        totalInList={totalInDrawerList}
        canNavigatePrev={canNavigatePrev}
        canNavigateNext={canNavigateNext}
        onNavigate={handleDrawerNavigate}
      />

      <ResolveUnmatchedModal
        open={resolveModalOpen}
        billingPeriodStart={billingPeriodStart}
        onClose={handleCloseResolveModal}
      />

      {reviewModal && (
        <ReviewBeforeSendModal
          open
          scope={reviewModal.scope}
          deliveryIds={reviewModal.ids}
          monthDeliveries={monthDeliveries}
          onClose={handleCloseReview}
          onSent={handleReviewSent}
        />
      )}
    </>
  )
}

const BulkInvoiceUpload: React.FC = () => {
  const { router, params, location } = useRouterContext()
  const { flags } = useFeatureFlags()
  const canGenerate = flags['admin.finance.invoice-generation-enabled'] === true
  const isStripeIntegrationEnabled =
    flags['admin.finance.stripe-integration-enabled'] === true

  // Mode lives in the URL:
  // /finance/invoice-management/{generation|delivery|delivery-and-payment}.
  // Anything unrecognized falls back to generation, unless generation is gated
  // off by the feature flag, in which case delivery is the only available mode.
  const requestedMode: InvoiceManagementMode = isInvoiceManagementMode(
    params?.mode
  )
    ? params.mode
    : 'generation'
  const mode: InvoiceManagementMode =
    !canGenerate && requestedMode === 'generation' ? 'delivery' : requestedMode

  // Keep the URL in sync when generation is gated off, so a deep link to
  // /generation lands on /delivery rather than showing delivery under a
  // generation URL (which would also break the generation invoice drawer).
  useEffect(() => {
    if (mode !== requestedMode) {
      router?.replace({
        pathname: `${FINANCE_PATH}/invoice-management/${mode}`,
        query: location?.query ?? {},
      })
    }
  }, [mode, requestedMode, router, location?.query])

  const goToMode = (next: InvoiceManagementMode) => {
    if (next === mode) return
    // Preserve the query (e.g. the shared `month`) across mode switches.
    router?.push({
      pathname: `${FINANCE_PATH}/invoice-management/${next}`,
      query: location?.query ?? {},
    })
  }

  const modeTabs: TabsProps['items'] = [
    ...(canGenerate ? [{ key: 'generation', label: 'Generation' }] : []),
    isStripeIntegrationEnabled
      ? { key: 'delivery-and-payment', label: 'Delivery and payment' }
      : { key: 'delivery', label: 'Delivery' },
  ]

  return (
    <PageContent>
      <Tabs
        activeKey={mode}
        items={modeTabs}
        onChange={(key) => {
          if (isInvoiceManagementMode(key)) goToMode(key)
        }}
        type="line"
      />

      {mode === 'delivery' && <DeliveryModeContent />}
      {mode === 'delivery-and-payment' && (
        <DeliveryModeContentWithStripePayment />
      )}
      {mode === 'generation' && <InvoiceGenerationView />}
    </PageContent>
  )
}

export { BulkInvoiceUpload }
